import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Loader2, Sparkles, Upload, X, ImageIcon } from "lucide-react";
import { useVisionBoard, type ReferencePhoto, type ReferenceKind } from "@/hooks/useVisionBoard";
import { useVisionBackdrop, buildBackdropRecord } from "@/hooks/useVisionBackdrop";
import { buildBackdropPrompt, type BackdropAnswers } from "@/utils/backdropPromptBuilder";
import type { AffirmationBackdrop, BackdropReference } from "@/types";
import { supabase } from "@/integrations/supabase/client";

interface BackdropBuilderProps {
  open: boolean;
  onClose: () => void;
  affirmation: string;
  onComplete: (backdrop: AffirmationBackdrop) => void;
  // When a new still image is generated, parent can show it immediately
  onPreviewImage?: (imageUrl: string) => void;
  // When the video finishes (or fails silently), parent can swap to it
  onAnimationDone?: (videoUrl: string | null) => void;
}

type Step = "scene" | "people" | "doing" | "mood" | "references" | "preview" | "generating";

const STEPS: { id: Step; title: string; subtitle: string; placeholder?: string; optional?: boolean }[] = [
  {
    id: "scene",
    title: "Describe the moment your affirmation is already true.",
    subtitle: "Where are you?",
    placeholder: "e.g. On the deck of a quiet beach house at sunrise, ocean in the distance",
  },
  {
    id: "doing",
    title: "What are you doing in this scene?",
    subtitle: "An action or a stillness — anything that captures the moment.",
    placeholder: "e.g. Sipping coffee, smiling at a notification on my phone confirming the sale",
  },
  {
    id: "people",
    title: "Who is with you?",
    subtitle: "Optional. Skip if you're alone.",
    placeholder: "e.g. My partner Sarah, our dog",
    optional: true,
  },
  {
    id: "mood",
    title: "What's the mood, light, and time of day?",
    subtitle: "Helps the image feel like the right kind of moment.",
    placeholder: "e.g. Golden hour, warm and calm, dreamy",
  },
];


const KIND_LABEL: Record<ReferenceKind, string> = {
  person: "Person",
  place: "Place",
  object: "Object",
};

const BackdropBuilder: React.FC<BackdropBuilderProps> = ({
  open,
  onClose,
  affirmation,
  onComplete,
  onPreviewImage,
  onAnimationDone,
}) => {
  const [step, setStep] = useState<Step>("scene");
  const [answers, setAnswers] = useState<BackdropAnswers>({
    affirmation,
    scene: "",
    people: "",
    doing: "",
    mood: "",
  });

  const [selectedRefIds, setSelectedRefIds] = useState<Set<string>>(new Set());
  const [promptDraft, setPromptDraft] = useState<string>("");
  const [polishing, setPolishing] = useState(false);
  const [uploading, setUploading] = useState(false);

  const { referencePhotos, addReferencePhoto, saveVideo } = useVisionBoard();
  const backdrop = useVisionBackdrop();

  const persistImageToBoard = async (imageUrl: string, prompt: string): Promise<string | null> => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const uid = sessionData.session?.user.id;
      if (!uid) return null;
      const id = crypto.randomUUID();
      const name = `Vision backdrop: ${prompt.slice(0, 40)}`;
      await supabase.from("vision_board_items").insert({
        id,
        user_id: uid,
        name,
        url: imageUrl,
        is_ai_generated: true,
        prompt,
        type: "image",
        created_at: new Date().toISOString(),
      });
      return name;
    } catch (e) {
      console.warn("Could not save backdrop image to Vision Board:", e);
      return null;
    }
  };

  // Reset on open
  useEffect(() => {
    if (open) {
      setStep("scene");
      setAnswers({ affirmation, scene: "", people: "", doing: "", mood: "" });
      setSelectedRefIds(new Set());
      setPromptDraft("");
      backdrop.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, affirmation]);

  const currentStepDef = useMemo(() => STEPS.find((s) => s.id === step), [step]);
  const stepIndex = STEPS.findIndex((s) => s.id === step);

  const handleAnswerChange = (value: string) => {
    if (!currentStepDef) return;
    setAnswers((prev) => ({ ...prev, [currentStepDef.id]: value }));
  };

  const currentAnswerValue = currentStepDef ? (answers as any)[currentStepDef.id] || "" : "";

  const canAdvance = () => {
    if (!currentStepDef) return false;
    if (currentStepDef.optional) return true;
    return currentAnswerValue.trim().length >= 3;
  };

  const goNext = () => {
    if (step === "scene") setStep("doing");
    else if (step === "doing") setStep("people");
    else if (step === "people") setStep("mood");
    else if (step === "mood") setStep("references");
  };

  const goBack = () => {
    if (step === "doing") setStep("scene");
    else if (step === "people") setStep("doing");
    else if (step === "mood") setStep("people");
    else if (step === "references") setStep("mood");
    else if (step === "preview") setStep("references");
  };


  const buildSelectedRefs = (): BackdropReference[] =>
    referencePhotos
      .filter((r) => selectedRefIds.has(r.id))
      .map((r) => ({ url: r.url, label: r.label, kind: r.kind }));

  const goToPreview = async () => {
    const assembled = buildBackdropPrompt(answers);
    setPromptDraft(assembled);
    setStep("preview");

    // Polish the prompt in the background
    setPolishing(true);
    try {
      const { data, error } = await supabase.functions.invoke("assist-vision-prompt", {
        body: { prompt: assembled },
      });
      if (!error && data?.improvedPrompt) {
        setPromptDraft(data.improvedPrompt);
      }
    } catch (e) {
      // Keep raw assembled prompt
    } finally {
      setPolishing(false);
    }
  };

  const handleGenerate = async () => {
    setStep("generating");
    const refs = buildSelectedRefs();
    const url = await backdrop.generateImage({ prompt: promptDraft, references: refs });
    if (!url) {
      // Failed — back to preview to retry
      setStep("preview");
      return;
    }
    onPreviewImage?.(url);

    // Save the still image to the Vision Board so the user can reuse it later
    const savedName = await persistImageToBoard(url, promptDraft);

    // Commit a backdrop record (without video yet) so chat can start immediately
    const record = buildBackdropRecord({
      prompt: promptDraft,
      imageUrl: url,
      references: refs,
    });
    onComplete(record);
    onClose();

    // Animate in background; final url is reported via onAnimationDone
    // If Kling fails, we silently keep the still image (onAnimationDone won't be called or will receive null)
    try {
      const videoUrl = await backdrop.animate(url, promptDraft);
      onAnimationDone?.(videoUrl);
      if (videoUrl) {
        await saveVideo(videoUrl, savedName || `Vision backdrop: ${promptDraft.slice(0, 40)}`);
      }
    } catch (e) {
      onAnimationDone?.(null);
    }
  };

  const handleUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    kind: ReferenceKind
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const defaultLabel = file.name.replace(/\.[^.]+$/, "").slice(0, 40);
      const ref = await addReferencePhoto(file, defaultLabel, kind);
      if (ref) {
        setSelectedRefIds((prev) => new Set(prev).add(ref.id));
      }
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl bg-background/80 backdrop-blur-2xl border-border/40">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Create your vision backdrop
          </DialogTitle>
          <DialogDescription>
            We'll generate a scene that reflects the reality where{" "}
            <span className="font-medium text-foreground">"{affirmation}"</span>{" "}
            is already true, and use it as your chat backdrop.
          </DialogDescription>
        </DialogHeader>

        {/* Guided question steps */}
        {currentStepDef && (
          <div className="space-y-3 py-2">
            <div className="text-xs text-muted-foreground">
              Step {stepIndex + 1} of {STEPS.length}
              {currentStepDef.optional && " · optional"}
            </div>
            <div>
              <h3 className="text-base font-medium">{currentStepDef.title}</h3>
              <p className="text-sm text-muted-foreground mt-1">{currentStepDef.subtitle}</p>
            </div>
            <Textarea
              value={currentAnswerValue}
              onChange={(e) => handleAnswerChange(e.target.value)}
              placeholder={currentStepDef.placeholder}
              rows={3}
              className="text-base resize-none"
              autoFocus
            />
            <div className="flex justify-between gap-2 pt-2">
              <Button variant="ghost" onClick={stepIndex === 0 ? onClose : goBack}>
                {stepIndex === 0 ? "Cancel" : "Back"}
              </Button>
              <div className="flex gap-2">
                {currentStepDef.optional && (
                  <Button variant="outline" onClick={goNext}>
                    Skip
                  </Button>
                )}
                <Button onClick={goNext} disabled={!canAdvance()}>
                  Next
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* References */}
        {step === "references" && (
          <div className="space-y-4 py-2">
            <div>
              <h3 className="text-base font-medium">Add reference photos (optional)</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Select people, places, or objects that should appear accurately in your scene.
              </p>
            </div>

            {referencePhotos.length > 0 ? (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-56 overflow-y-auto">
                {referencePhotos.map((ref) => {
                  const selected = selectedRefIds.has(ref.id);
                  return (
                    <button
                      key={ref.id}
                      type="button"
                      onClick={() =>
                        setSelectedRefIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(ref.id)) next.delete(ref.id);
                          else next.add(ref.id);
                          return next;
                        })
                      }
                      className={`relative aspect-square rounded-xl overflow-hidden border-2 transition ${
                        selected ? "border-primary ring-2 ring-primary/40" : "border-border/30"
                      }`}
                    >
                      <img src={ref.url} alt={ref.label} className="w-full h-full object-cover" />
                      <div className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[10px] py-0.5 px-1 truncate text-left">
                        {KIND_LABEL[ref.kind]} · {ref.label}
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground border border-dashed border-border/40 rounded-xl p-4 flex items-center gap-2">
                <ImageIcon className="h-4 w-4" />
                No saved references yet. Upload one below.
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              {(["person", "place", "object"] as ReferenceKind[]).map((kind) => (
                <label
                  key={kind}
                  className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-full bg-muted/40 hover:bg-muted/60 cursor-pointer"
                >
                  <Upload className="h-3 w-3" />
                  Upload {KIND_LABEL[kind].toLowerCase()}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={uploading}
                    onChange={(e) => handleUpload(e, kind)}
                  />
                </label>
              ))}
              {uploading && <Loader2 className="h-4 w-4 animate-spin self-center" />}
            </div>

            <div className="flex justify-between gap-2 pt-2">
              <Button variant="ghost" onClick={goBack}>Back</Button>
              <Button onClick={goToPreview}>
                {selectedRefIds.size > 0
                  ? `Continue with ${selectedRefIds.size} reference${selectedRefIds.size > 1 ? "s" : ""}`
                  : "Continue without references"}
              </Button>
            </div>
          </div>
        )}

        {/* Preview prompt */}
        {step === "preview" && (
          <div className="space-y-3 py-2">
            <div>
              <h3 className="text-base font-medium">Review your scene</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Edit anything you'd like before we generate the image.
              </p>
            </div>
            <Textarea
              value={promptDraft}
              onChange={(e) => setPromptDraft(e.target.value)}
              rows={7}
              className="text-sm font-mono leading-relaxed"
              disabled={polishing}
            />
            {polishing && (
              <p className="text-xs text-muted-foreground flex items-center gap-2">
                <Loader2 className="h-3 w-3 animate-spin" /> Polishing your prompt…
              </p>
            )}
            <div className="flex justify-between gap-2 pt-2">
              <Button variant="ghost" onClick={goBack}>Back</Button>
              <Button onClick={handleGenerate} disabled={polishing || promptDraft.trim().length < 30}>
                <Sparkles className="h-4 w-4 mr-1" />
                Generate backdrop
              </Button>
            </div>
          </div>
        )}

        {/* Generating */}
        {step === "generating" && (
          <div className="py-10 flex flex-col items-center gap-3 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-base font-medium">Generating your vision…</p>
            <p className="text-sm text-muted-foreground max-w-sm">
              We're rendering your scene. This usually takes 15–25 seconds. The animated motion
              will continue building in the background after the image lands.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BackdropBuilder;
